import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { CustomerProfile } from '@emazy/shared-types';

import { routes } from '@/config/routes';
import { useAuth } from '@/features/auth/context';
import { hasAnyRole } from '@/features/auth/utils';
import { customerProfileKeys, type CustomerProfileApi } from '@/features/account/api';
import { useOptionalCustomerProfileApi } from '@/features/account/context';

export function AccountMenu() {
  const profileApi = useOptionalCustomerProfileApi();
  return profileApi ? <ProfileAwareAccountMenu api={profileApi} /> : <AccountMenuView />;
}

function ProfileAwareAccountMenu({ api }: { api: CustomerProfileApi }) {
  const { status } = useAuth();
  const profile = useQuery({
    queryKey: customerProfileKeys.profile(),
    queryFn: () => api.getCustomerProfile(),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });
  return <AccountMenuView accountProfile={profile.data?.profile} />;
}

function AccountMenuView({ accountProfile }: { accountProfile?: CustomerProfile | undefined }) {
  const { status, user } = useAuth();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function close(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function escape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  if (status === 'initializing') {
    return (
      <span className="icon-button header-actions__desktop" aria-label="Loading account">
        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
      </span>
    );
  }

  return (
    <div className="account-menu header-actions__desktop" ref={rootRef}>
      <button
        className="account-menu__trigger"
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((value) => !value)}
      >
        <i className="pi pi-user" aria-hidden="true" />
        <span>{accountProfile?.firstName ?? user?.firstName ?? 'Account'}</span>
        <i className="pi pi-chevron-down" aria-hidden="true" />
      </button>
      {open && (
        <div className="account-menu__panel" role="menu">
          {user ? (
            <>
              <div className="account-menu__identity">
                <strong>{accountProfile?.displayName ?? user.displayName}</strong>
                <small>{accountProfile?.email ?? user.email}</small>
              </div>
              <NavLink role="menuitem" to={routes.dashboard} onClick={() => setOpen(false)}>
                Dashboard
              </NavLink>
              <NavLink role="menuitem" to={routes.orders} onClick={() => setOpen(false)}>
                Orders
              </NavLink>
              <NavLink role="menuitem" to={routes.accountProfile} onClick={() => setOpen(false)}>
                Account settings
              </NavLink>
              {hasAnyRole(user, ['admin']) && (
                <NavLink role="menuitem" to={routes.admin} onClick={() => setOpen(false)}>
                  Admin portal
                </NavLink>
              )}
              {hasAnyRole(user, ['admin', 'workshop']) && (
                <NavLink role="menuitem" to={routes.workshop} onClick={() => setOpen(false)}>
                  Workshop portal
                </NavLink>
              )}
              <NavLink
                role="menuitem"
                className="account-menu__logout"
                to={routes.logout}
                onClick={() => setOpen(false)}
              >
                Sign out
              </NavLink>
            </>
          ) : (
            <>
              <NavLink role="menuitem" to={routes.login} onClick={() => setOpen(false)}>
                Sign in
              </NavLink>
              <NavLink role="menuitem" to={routes.register} onClick={() => setOpen(false)}>
                Create account
              </NavLink>
            </>
          )}
        </div>
      )}
    </div>
  );
}
